/**
 * Read Aloud result panel — EAA-1890.
 *
 * Renders what `speakingScoreService.score` returned for each prompt the way
 * interactive-reading does: the reference script with every word coloured by its
 * quality bucket (score + phone breakdown on hover), above an overall 0-100 card
 * tinted by pass/fail. The two content types share `SyncedPassage` and
 * `normalizePronunciationScore`, so the same recording reads the same in both.
 *
 * `SpeakingAiGradePanel` deliberately stays for the other QT_SPEAK_* types: the
 * criteria API returns bands and prose, which has no per-word layer to draw.
 */

import * as React from 'react';
import map from 'lodash/map';
import compact from 'lodash/compact';
import {
    cn,
    SyncedPassage,
    tiptapJsonToPlainText,
    isTiptapDocument,
    type SyncedWordScore,
} from '@ai-content-hub/content-kit';
import { normalizePronunciationScore } from '@ai-content-hub/shared-api';
import type { SpeakingItem } from './schema';
import type { SpeakingPronunciationEntry } from './scorer';

export interface SpeakingPronunciationPanelProps {
    details: Record<string, unknown>;
    items: SpeakingItem[];
    /** 0-1 pass mark; tints the score card. */
    passingThreshold?: number;
    className?: string;
    t: (key: string, fallback?: string) => string;
}

function isPronunciationEntry(value: unknown): value is SpeakingPronunciationEntry {
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
        return false;
    }
    const entry = value as Record<string, unknown>;
    return typeof entry.referenceText === 'string' && typeof entry.result === 'object';
}

function SpeakingPronunciationBlock({
    entry,
    heading,
    passingThreshold,
    t,
}: {
    entry: SpeakingPronunciationEntry;
    heading: string;
    passingThreshold: number;
    t: (key: string, fallback?: string) => string;
}): React.ReactElement {
    const score = React.useMemo(
        () => normalizePronunciationScore(entry.result, passingThreshold),
        [entry.result, passingThreshold]
    );

    const wordScores: SyncedWordScore[] | undefined = score.words.length
        ? map(score.words, (w) => ({
              word: w.word,
              bucket: w.bucket,
              score: w.quality,
              phones: w.phones,
          }))
        : undefined;

    return (
        <div className="speaking-player-pronunciation-block space-y-3">
            <h4 className="speaking-player-pronunciation-block-title text-base font-semibold text-foreground">
                {heading}
            </h4>

            <div className="speaking-player-pronunciation-passage space-y-1">
                <p className="text-xs font-medium text-muted-foreground">
                    {t('pronunciationReference', 'What you were asked to read')}
                </p>
                <SyncedPassage
                    text={entry.referenceText}
                    wordScores={wordScores}
                    scoreLabel={t('pronunciationWordScore', 'Score')}
                    className="speaking-player-pronunciation-text text-base leading-relaxed"
                />
            </div>

            {wordScores ? (
                <ul className="speaking-player-pronunciation-legend flex flex-wrap gap-4 list-none p-0 m-0 text-xs text-muted-foreground">
                    <li className="flex items-center gap-1.5">
                        <span className="h-2 w-2 rounded-full bg-success" aria-hidden />
                        {t('pronunciationLegendGood', 'Clear')}
                    </li>
                    <li className="flex items-center gap-1.5">
                        <span className="h-2 w-2 rounded-full bg-warning" aria-hidden />
                        {t('pronunciationLegendOk', 'Almost')}
                    </li>
                    <li className="flex items-center gap-1.5">
                        <span className="h-2 w-2 rounded-full bg-destructive" aria-hidden />
                        {t('pronunciationLegendBad', 'Needs work')}
                    </li>
                </ul>
            ) : null}

            <div
                className={cn(
                    'speaking-player-pronunciation-score flex items-center justify-center gap-3 rounded-sm border p-3',
                    score.passed
                        ? 'border-success/40 bg-success/10'
                        : 'border-destructive/40 bg-destructive/10'
                )}
            >
                <span className="text-sm text-muted-foreground">
                    {t('pronunciationYourScore', 'Your score')}
                </span>
                <span
                    className={cn(
                        'text-2xl font-semibold tabular-nums',
                        score.passed ? 'text-success' : 'text-destructive'
                    )}
                >
                    {score.overall}
                    <span className="text-base font-normal text-muted-foreground">/100</span>
                </span>
            </div>
        </div>
    );
}

export function SpeakingPronunciationPanel({
    details,
    items,
    passingThreshold = 0.7,
    className,
    t,
}: SpeakingPronunciationPanelProps): React.ReactElement | null {
    if (details.pronunciationGrading !== true) {
        return null;
    }
    const raw = details.pronunciationResults;
    if (!Array.isArray(raw) || raw.length === 0) {
        return null;
    }

    const titleById = new Map(
        map(items, (item) => {
            const title = item.data?.title;
            if (title && typeof title === 'object' && isTiptapDocument(title)) {
                return [item.id, tiptapJsonToPlainText(title).trim()] as const;
            }
            return [item.id, (typeof title === 'string' ? title : '').trim()] as const;
        })
    );

    return (
        <div
            className={cn(
                'speaking-player-pronunciation border-0 shadow-sm rounded-xl bg-card p-4 md:p-6 space-y-8',
                className
            )}
        >
            {compact(
                map(raw, (value: unknown, idx: number) => {
                    if (!isPronunciationEntry(value)) {
                        return null;
                    }
                    const titleHint = titleById.get(value.itemId) ?? '';
                    const heading =
                        raw.length > 1
                            ? `${idx + 1}. ${titleHint || t('aiGradingPrompt', 'Speaking prompt')}`
                            : t('pronunciationResults', 'Pronunciation results');
                    return (
                        <SpeakingPronunciationBlock
                            key={`${value.itemId || 'r'}-${idx}`}
                            entry={value}
                            heading={heading}
                            passingThreshold={passingThreshold}
                            t={t}
                        />
                    );
                })
            )}
        </div>
    );
}

export default SpeakingPronunciationPanel;
